import React, { createRef, ReactElement, ReactNode } from 'react'
import { Flex, Embed } from 'theme-ui'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { AlertContextProvider } from 'ui'
import useTutorialSlides from 'components/Dashboard/utils/useTutorialSlides'
import useQueryString from 'utils/useQueryString'
import { useLocation, useMatch } from '@reach/router'
import ConfirmEmailScreen from 'components/Organization/ConfirmEmailScreen'
import TwoFaModalDialog from 'components/Organization/TwoFa/TwoFaModalDialog'
import OrganizationNavBarContent, {
  OrganizationNavRelativePath,
} from 'components/Organization/OrganizationNavBar/OrganizationNavBarContent'
import { userApi } from 'store/api/user'
import DashboardTutorial from 'components/Dashboard/Tutorial/DashboardTutorial'
import NavBar, { navBarDesktopCollapsedWidth, navBarTabletWidth } from 'components/Dashboard/NavBar'
import NotificationsDialog from 'components/Dashboard/NavBar/NotificationsDialog'
import SetupGuide from 'components/SetupGuide/SetupGuide'
import { useIsTwoFaVisible } from 'utils/useIsTwoFaVisible'
import BarryDialog from 'components/Barry/BarryComponents/BarryDialog'
import CreatePortalWrapper from 'components/CreatePortalWrapper'
import useGetColorMode from 'utils/useGetColorMode'
import Popup from 'ui/Popup'
import { setIsOnboardVideoVisible } from 'store/dashboard/dashboard'
import NavBarContent from './NavBar/NavBarContent'
import NavMenuMobile from './NavBar/NavMenu.mobile'

export const scrollingContainerRef = createRef<HTMLDivElement>()

export default function DashboardScrollingContainer({
  children,
}: {
  children: ReactNode
}): ReactElement {
  const breakpoints = useBreakpointIndex()
  const isMobile = breakpoints === 0

  const isInMaintenanceMode = useMatch('maintenance-mode')
  const is404Page = useMatch('404')
  const { isLightMode } = useGetColorMode()

  return (
    <Flex
      data-testid="dashboard-scrolling-container"
      ref={scrollingContainerRef}
      sx={{
        position: 'fixed',
        width: '100vw',
        height: '100%',
        backgroundColor: 'blue800',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: 'auto',
        fontFamily: 'body',
        scrollbarGutter: 'stable',
      }}
      className={`${isLightMode ? 'light' : 'dark'} show-scrollbar`}
    >
      <Flex
        sx={{
          maxWidth: '1512px',
          width: '100%',
          height: '100%',
          opacity: breakpoints === -1 ? 0 : 1,
        }}
      >
        {!isMobile && !is404Page && !isInMaintenanceMode && <NavBar />}

        <DashboardContent>{children}</DashboardContent>
      </Flex>

      <DashboardModalDialogs />
      <OnboardVideoPopup />
    </Flex>
  )
}

function DashboardContent({ children }: { children: ReactNode }) {
  const isNavBarExpanded = useAppSelector(s => s.dashboard.isNavBarExpanded)

  return (
    <Flex
      sx={{
        position: 'relative',
        width: '100%',
        flexDirection: 'column',
        // should be the same as the nav bar width
        ml: [0, navBarTabletWidth, isNavBarExpanded ? '29.8rem' : navBarDesktopCollapsedWidth],
        transition: 'margin-left 0.3s',
      }}
    >
      {children}
    </Flex>
  )
}

function DashboardModalDialogs(): ReactElement {
  const { qs } = useQueryString()
  const location = useLocation()
  const { currentTutorial } = useTutorialSlides()
  const isSetupGuideVisible = useAppSelector(s => !!s.session.sessionToken) && qs.deviceId
  const { data: userData } = userApi.endpoints.getUser.useQueryState('')
  const isConfirmEmailVisible = !!userData?.org?.PK && !userData?.email_status
  const isTwoFaVisible = useIsTwoFaVisible()
  const isOrganization = useAppSelector(s => s.session.isOrganization)

  return (
    <>
      {isConfirmEmailVisible &&
        !location.pathname.includes(OrganizationNavRelativePath.ACCOUNT) && <ConfirmEmailScreen />}
      {isTwoFaVisible && <TwoFaModalDialog />}

      {isSetupGuideVisible && (
        <AlertContextProvider>
          <SetupGuide />
        </AlertContextProvider>
      )}

      {!isConfirmEmailVisible && !isTwoFaVisible && (
        <DashboardTutorial tutorialType={currentTutorial} />
      )}

      <NotificationsDialog />

      <NavMenuMobile>
        {isOrganization ? <OrganizationNavBarContent /> : <NavBarContent />}
      </NavMenuMobile>

      <CreatePortalWrapper>
        <BarryDialog />
      </CreatePortalWrapper>
    </>
  )
}

function OnboardVideoPopup(): ReactElement | null {
  const dispatch = useAppDispatch()
  const isOnboardVideoVisible = useAppSelector(s => s.dashboard.isOnboardVideoVisible)

  return isOnboardVideoVisible ? (
    <Popup
      dismiss={() => {
        dispatch(setIsOnboardVideoVisible(false))
      }}
    >
      <Embed src={`https://www.youtube.com/embed/i7CBnPwiyt0?si=VmeI5uqNSyKM_XF5`} />
    </Popup>
  ) : null
}
